/**
 * @file Systemes d'etat : machine a etats du mode, decroissance des flashs/morts, timers (buffs, tickets, missions, auto-upgrade, temps de jeu).
 */
import type { SimContext } from '../Context';
import type { System } from './System';

/** Avance la machine a etats du mode courant (spawns, chrono, victoire/defaite). */
export const ModeSystem: System = {
  id: 'mode',
  update(ctx: SimContext, dt: number): void {
    ctx.modes.update(dt);
  },
};

/**
 * Decroissance des flashs de coup, animation de mort (dying) puis destruction, invulnerabilite.
 * La grille est deja nettoyee par killTarget ; le hook World.onDestroy la nettoie aussi par securite.
 */
export const VisualStatusSystem: System = {
  id: 'visualStatus',
  update(ctx: SimContext, dt: number): void {
    for (const e of ctx.rt.world.all()) {
      if (!e.alive) continue;
      const v = e.visual;
      if (v) {
        if (v.hitFlash > 0) v.hitFlash = Math.max(0, v.hitFlash - dt * 6);
        if (v.dying > 0) {
          v.dying -= dt;
          if (v.dying <= 0) {
            v.dying = 0;
            ctx.rt.world.destroy(e.id);
            continue;
          }
        }
      }
      if (e.health && e.health.invulnerable > 0) e.health.invulnerable = Math.max(0, e.health.invulnerable - dt);
    }
  },
};

/** Accumulateur de 1 s par simulation (pas d'etat global partage entre simulations). */
const secondAcc = new WeakMap<SimContext, number>();

/**
 * Timers "temps de jeu" et horloge murale (tourne aussi en pause) : buffs, auto-upgrade, temps de jeu, puis toutes les secondes
 * missions, tickets, saison de passe, derniere presence.
 */
export const TimersSystem: System = {
  id: 'timers',
  runWhenPaused: true,
  update(ctx: SimContext, dt: number): void {
    const s = ctx.services;
    s.buffs.tick(dt);
    s.upgrades.autoUpgrade(dt);
    s.weapons.tickAutoMerge(dt);
    ctx.data.run.playTimeSeconds += dt;
    const acc = (secondAcc.get(ctx) ?? 0) + dt;
    if (acc < 1) {
      secondAcc.set(ctx, acc);
      return;
    }
    secondAcc.set(ctx, acc - 1);
    s.missions.refresh(ctx.rt.wallMs);
    s.tickets.regen(ctx.rt.wallMs);
    s.pass.refreshSeason(ctx.rt.wallMs);
    ctx.data.meta.lastSeenMs = ctx.rt.wallMs;
  },
};
